import { useEffect } from 'react';
import { type AnyFieldApi, useForm } from '@tanstack/react-form';

import { Button, Icon, Modal, Text, TextareaField } from '@nvision/ui';

import { ContractStatusField } from '@/modules/entities';

import { useChangeContractStatus } from '../../../hooks';
import { type ChangeContractStatusFormValues, changeContractStatusFormSchema } from '../../../models';

import styles from './change-status-dialog.module.css';

interface ChangeStatusDialogProps {
  contractKey?: string;
  currentStatus?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const getDefaultValues = (currentStatus?: string | null): ChangeContractStatusFormValues => ({
  comment: '',
  status: currentStatus ? { id: currentStatus, label: currentStatus } : null,
});

export const ChangeStatusDialog: React.FC<ChangeStatusDialogProps> = ({
  contractKey,
  currentStatus,
  isOpen,
  onClose,
}) => {
  const { isPending, mutate: changeContractStatus } = useChangeContractStatus({ onSuccess: onClose });

  const form = useForm({
    defaultValues: getDefaultValues(currentStatus),
    onSubmit: async ({ value }) => {
      if (!contractKey || !value.status) {
        return;
      }

      changeContractStatus({ comment: value.comment, contractKey, status: String(value.status.id) });
    },
    validators: {
      onChange: changeContractStatusFormSchema,
      onSubmit: changeContractStatusFormSchema,
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(getDefaultValues(currentStatus), { keepDefaultValues: false });
    }
  }, [currentStatus, form, isOpen]);

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: { message?: string }) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} className={styles.modal}>
      <Modal.Header className={styles.header}>
        <Text size="xlg" weight="semibold">
          Change Status
        </Text>
        <Icon name="x" cursor="pointer" size={20} onClick={onClose} />
      </Modal.Header>

      <Modal.Content className={styles.content}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <form.Field name="status">
            {(field) => (
              <ContractStatusField
                value={field.state.value ?? null}
                onSelectionChange={field.handleChange}
                label="Contract Status"
                size="sm"
                isRequired
                errorMessage={getError(field)}
              />
            )}
          </form.Field>

          <form.Field name="comment">
            {(field) => (
              <TextareaField
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                label="Comments"
                placeholder="Enter"
                size="sm"
                rows={5}
              />
            )}
          </form.Field>

          <button type="submit" style={{ display: 'none' }}>
            Hidden Submit
          </button>
        </form>
      </Modal.Content>

      <Modal.Footer className={styles.footer}>
        <Button secondary outline onPress={onClose}>
          Cancel
        </Button>
        <Button primary isLoading={isPending} onClick={() => form.handleSubmit()}>
          Save changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
